import React, { useState } from 'react';

import { Box } from '@clutter/clean';

import { NavLinkButton, Badge, Table, Modal, Alert, Button, AnchorButton } from '@shared/components/bootstrap';
import { Currency, Timestamp } from '@shared/components/helpers';

import { Panel } from '@admin/components/helpers/panel';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import {
  useStorageTreasuresAuctionListQuery,
  StorageTreasures__AuctionStatus,
  StorageTreasuresAuctionListQuery,
  useStorageTreasuresAuctionCancelMutation,
  Status,
  StorageTreasuresAuctionListDocument,
  useStorageTreasuresAuctionAdvertisementConfirmationMutation,
  useStorageTreasuresAuctionListFiltersFormDataQuery,
} from '@admin/schema';

import { AuctionFilters, AuctionFilterValues } from './filters';

const AUCTION_STATUS_TO_STYLE: Record<
  StorageTreasures__AuctionStatus,
  'danger' | 'warning' | 'success' | 'info' | 'secondary'
> = {
  [StorageTreasures__AuctionStatus.Draft]: 'secondary',
  [StorageTreasures__AuctionStatus.Active]: 'info',
  [StorageTreasures__AuctionStatus.Cancelled]: 'warning',
  [StorageTreasures__AuctionStatus.Sold]: 'success',
  [StorageTreasures__AuctionStatus.Unsold]: 'danger',
};

const CANCELLATION_REASONS = [
  '[User] Bankruptcy declared',
  '[User] Customer deceased',
  '[User] Payment - Full balance',
  '[User] Payment - Partial',
  '[User] Payment - PTS',
  '[User] Payment - PTV',
  '[Failure] RTS & notice email undelivered',
  'Other',
];

const StatusBadge: React.FC<{
  auction: StorageTreasuresAuction;
}> = ({ auction }) => {
  const {
    status,
    startTime,
    noticeLetterPending,
    noticeEmailPending,
    payToVacateEmailPending,
    advertisementConfirmationPending,
  } = auction;

  // Determine if auction has started (for Active status)
  const hasStarted = status === StorageTreasures__AuctionStatus.Active && new Date(startTime) <= new Date();

  // Get the main status text
  const getStatusText = () => {
    if (status === StorageTreasures__AuctionStatus.Active) {
      return hasStarted ? 'ACTIVE (STARTED)' : 'ACTIVE (NOT STARTED)';
    }
    return status.toUpperCase();
  };

  // Get pending tasks for draft auctions
  const getPendingTasks = () => {
    if (status !== StorageTreasures__AuctionStatus.Draft) return [];

    const tasks = [];
    if (noticeLetterPending) tasks.push('Notice Letter Pending');
    if (noticeEmailPending) tasks.push('Notice Email Pending');
    if (payToVacateEmailPending) tasks.push('Pay-to-Vacate Email Pending');
    if (advertisementConfirmationPending) tasks.push('Advertisement Confirmation Pending');
    if (tasks.length === 0) tasks.push('Activation Triggered');

    return tasks;
  };

  const pendingTasks = getPendingTasks();

  return (
    <Box>
      <Badge style={AUCTION_STATUS_TO_STYLE[status]}>{getStatusText()}</Badge>
      {pendingTasks.length > 0 && (
        <Box margin="4px 0 0">
          {pendingTasks.map((task, index) => (
            <Box key={index} margin="0 0 2px">
              <Badge style="warning">{task}</Badge>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

type StorageTreasuresAuction = StorageTreasuresAuctionListQuery['paginated']['results'][0];

const Row: React.FC<{ auction: StorageTreasuresAuction; setAuctionToCancel: () => void }> = ({
  auction,
  setAuctionToCancel,
}) => (
  <tr>
    <td>{auction.id}</td>
    <td>
      <NavLinkButton kind="primary" to={`/accounts/${auction.auctionedAccount.id}`} target="_blank">
        {auction.auctionedAccount.name}
      </NavLinkButton>
    </td>
    <td>{auction.warehouse.name}</td>
    <td>{<Timestamp value={auction.startTime} format={Timestamp.Format.DateTime} />}</td>
    <td>{<Timestamp value={auction.endTime} format={Timestamp.Format.DateTime} />}</td>
    <td>
      <Box>
        <Box>
          Delinquent Balance:{' '}
          {auction.delinquentBalanceAtCreation ? (
            <Currency value={auction.delinquentBalanceAtCreation} currency={auction.currency} />
          ) : (
            'Not Recorded'
          )}
        </Box>
        <Box>
          PTV:{' '}
          {auction.payToVacateOfferAmount ? (
            <Currency value={auction.payToVacateOfferAmount} currency={auction.currency} />
          ) : (
            'Not Recorded'
          )}
        </Box>
        <Box>
          PTV Expiration:{' '}
          {auction.payToVacateOfferExpiresAt ? (
            <Timestamp value={auction.payToVacateOfferExpiresAt} format={Timestamp.Format.Date} />
          ) : (
            'Not Recorded'
          )}
        </Box>
      </Box>
    </td>
    <td>
      {auction.winner && (
        <NavLinkButton kind="primary" to={`/accounts/${auction.winner.id}`} target="_blank">
          {auction.winner?.name}
        </NavLinkButton>
      )}
    </td>
    <td>{auction.winnerInvoice && <Currency value={auction.winnerInvoice.clutterAmount} />}</td>
    <td>
      {auction.winnerOrder && (
        <NavLinkButton kind="primary" to={`/orders/${auction.winnerOrder.id}`} target="_blank">
          {auction.winnerOrder.number}
        </NavLinkButton>
      )}
    </td>
    <td>
      <StatusBadge auction={auction} />
      {auction.status === StorageTreasures__AuctionStatus.Cancelled && (
        <Box textAlign="center" margin="4px 0 0">
          {auction.canceler && (
            <>
              By: {auction.canceler.name}
              <br />
            </>
          )}
          {auction.cancelReason}
        </Box>
      )}
    </td>
    <td className="col-zero text-center">
      <Box.Flex gap={'8px'}>
        {auction.storageTreasuresLink && (
          <AnchorButton kind="primary" href={auction.storageTreasuresLink} target="_blank">
            View
          </AnchorButton>
        )}
        {(auction.status === StorageTreasures__AuctionStatus.Active ||
          auction.status === StorageTreasures__AuctionStatus.Draft) && (
          <Button kind="warning" onClick={setAuctionToCancel}>
            Cancel
          </Button>
        )}
      </Box.Flex>
    </td>
  </tr>
);

const NoAuctions: React.FC = () => (
  <tr>
    <td colSpan={11} className="text-center text-muted" style={{ padding: '2rem' }}>
      No Auctions Found
    </td>
  </tr>
);

interface Props {
  auctionedAccountID?: string;
  hideFilters?: boolean;
  title?: string;
}

export const StorageTreasuresAuctionList: React.FC<Props> = ({
  auctionedAccountID,
  hideFilters = false,
  title = 'Storage Treasures',
}) => {
  const [page, setPage] = useState<number | undefined>();
  const [auctionToCancel, setAuctionToCancel] = useState<StorageTreasuresAuction | undefined>();
  const [filters, setFilters] = useState<AuctionFilterValues>({});
  const [showConfirmBulkAdvertisementModal, setShowConfirmBulkAdvertisementModal] = useState(false);

  const { data, loading } = useStorageTreasuresAuctionListQuery({
    client,
    variables: {
      page: page,
      status: filters.status,
      warehouseID: filters.warehouseID,
      minInvoiceAmount: filters.minInvoiceAmount ? parseFloat(filters.minInvoiceAmount) : undefined,
      date: filters.date,
      search: filters.search,
      auctionedAccountID: auctionedAccountID,
    },
  });
  const paginated = data?.paginated;

  const handleFilterUpdate = (newFilters: AuctionFilterValues) => {
    setFilters(newFilters);
    setPage(undefined); // Reset to first page when filtering
  };

  return (
    <>
      {!hideFilters && <AuctionFilters filters={filters} onChangeFilters={handleFilterUpdate} />}

      <Panel>
        <Panel.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Panel.Title>{title}</Panel.Title>
            <div style={{ display: 'flex', gap: '8px', marginRight: '1em' }}>
              <Button kind="primary" onClick={(_) => setShowConfirmBulkAdvertisementModal(true)}>
                Bulk Confirm Advertisements
              </Button>
            </div>
          </div>
        </Panel.Header>
        <Table responsive striped>
          <thead>
            <tr>
              <th>Teal ID</th>
              <th>Auctioned Account</th>
              <th>Facility</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Notice Letter Info</th>
              <th>Winner</th>
              <th>Winner Invoice Amount</th>
              <th>Winner Facility Pickup</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading ? (
              paginated?.results.length ? (
                paginated.results.map((auction) => (
                  <Row key={auction.id} auction={auction} setAuctionToCancel={() => setAuctionToCancel(auction)}></Row>
                ))
              ) : (
                <NoAuctions />
              )
            ) : undefined}
          </tbody>
        </Table>
        {loading && <Spinner />}
        {paginated && <Pagination pagination={paginated.pagination} onPage={setPage} />}
        {auctionToCancel && <CancelModal auction={auctionToCancel} onClose={() => setAuctionToCancel(undefined)} />}
        {showConfirmBulkAdvertisementModal && (
          <BulkAdvertisementConfirmationModal onClose={() => setShowConfirmBulkAdvertisementModal(false)} />
        )}
      </Panel>
    </>
  );
};

const CancelModal: React.FC<{
  auction: StorageTreasuresAuction;
  onClose(): void;
}> = ({ auction, onClose }) => {
  const [error, setError] = useState<string | undefined>();
  const [reason, setReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');

  const [cancel, { loading }] = useStorageTreasuresAuctionCancelMutation({
    client,
    refetchQueries: [
      {
        query: StorageTreasuresAuctionListDocument,
      },
    ],
  });

  const onConfirm = async () => {
    const response = await cancel({
      variables: {
        tealAuctionID: auction.id,
        reason: reason === 'Other' ? `[Other] - ${otherReason.trim()}` : reason,
      },
    });
    if (response && response.data?.storageTreasuresAuctionCancel) {
      if (response.data.storageTreasuresAuctionCancel.error) {
        setError(response.data.storageTreasuresAuctionCancel.error);
      } else if (response.data.storageTreasuresAuctionCancel.status === Status.Ok) {
        onClose();
      }
    }
  };

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            Cancel Auction ID: {auction.id} ({auction.auctionedAccount.name})
          </Modal.Title>
          <Modal.Close close={onClose} />
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert style="danger">
              <strong>Error: </strong>
              {error}
            </Alert>
          )}
          <p>Why do you want to cancel this auction?</p>
          <select
            className="form-control"
            value={reason === '-- Reasons --' ? '' : reason}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              setReason(event.target.value);
            }}
          >
            <option value="">-- Reasons --</option>
            {CANCELLATION_REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </select>

          {reason === 'Other' && (
            <input
              className="form-control"
              style={{ marginTop: '2rem' }}
              type="text"
              value={otherReason}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setOtherReason(event.target.value);
              }}
              placeholder="Enter reason here"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} disabled={loading} size="lg" kind="info">
            Nevermind
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading || reason.length === 0 || (reason === 'Other' && !otherReason.trim())}
            size="lg"
            kind="danger"
          >
            Cancel Auction
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const BulkAdvertisementConfirmationModal: React.FC<{
  onClose(): void;
}> = ({ onClose }) => {
  const [error, setError] = useState<string | undefined>();
  const [warehouseID, setWarehouseID] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [confirm, { loading }] = useStorageTreasuresAuctionAdvertisementConfirmationMutation({
    client,
    refetchQueries: [
      {
        query: StorageTreasuresAuctionListDocument,
      },
    ],
  });

  const onConfirm = async () => {
    const response = await confirm({
      variables: {
        warehouseID,
        startDate,
      },
    });
    if (response && response.data?.storageTreasuresAuctionAdvertisementConfirmation) {
      if (response.data.storageTreasuresAuctionAdvertisementConfirmation.error) {
        setError(response.data.storageTreasuresAuctionAdvertisementConfirmation.error);
      } else if (response.data.storageTreasuresAuctionAdvertisementConfirmation.status === Status.Ok) {
        onClose();
      }
    }
  };

  const { data } = useStorageTreasuresAuctionListFiltersFormDataQuery({ client });
  const warehouses = data?.warehouses || [];

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Bulk Action: Confirm Auction Advertisements</Modal.Title>
          <Modal.Close close={onClose} />
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert style="danger">
              <strong>Error: </strong>
              {error}
            </Alert>
          )}

          {data && (
            <>
              <div className="form-group">
                <label htmlFor="warehouseId">Auction Warehouse</label>
                <select
                  id="warehouseId"
                  name="Warehouse ID"
                  className="form-control"
                  value={warehouseID}
                  onChange={(e) => setWarehouseID(e.target.value)}
                  required
                >
                  <option value="">Select a warehouse...</option>
                  {warehouses.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="auction-start-date">Auction Start Date</label>
                <input
                  id="auction-start-date"
                  name="Auction Start Date"
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} disabled={loading} size="lg" kind="info">
            Nevermind
          </Button>
          <Button onClick={onConfirm} disabled={loading || !startDate || !warehouseID} size="lg" kind="danger">
            Confirm Auction Advertisements
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
